/**
 * Owns all client-side state for an in-flight server-side ("remote")
 * agent run.
 *
 * "Remote" means a run we did not initiate from the local Composer:
 * typically a replay re-attached after a refresh or in another tab.
 * Local POST runs leave `isRunningRemotely` false.
 *
 * Responsibilities:
 *  - On mount, if `/connect` reported the conversation as in-flight,
 *    arm the agent for replay and trigger the runtime's resume flow.
 *  - Track lifecycle events (RUN_STARTED / RUN_FINISHED / RUN_ERROR)
 *    to drive `isRunningRemotely` and the cross-conversation
 *    `running-conversations` registry.
 *  - Reload persisted history once a replay run settles, so the thread
 *    reflects the canonical post-run state.
 *  - Expose the cancel button handler.
 */
import { useCallback, useEffect, useState } from "react";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { ConversationStatus } from "~/components/assistant/types";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { markConversationIdle, markConversationRunning } from "./running-conversations";

interface UseRemoteRunOptions {
  agent: ReplayHttpAgent;
  runtime: AssistantRuntime;
  api: AssistantApi;
  conversationId: string;
  /** Conversation status from /connect; drives replay attach on mount. */
  status: ConversationStatus;
  /**
   * Pre-converted /connect messages (incl. the in-flight user prompt).
   * Used to anchor `resumeRun`'s parentId on the seeded prompt's id —
   * `runtime.thread.getState()` is stale immediately after `reset()`
   * because the external-store core syncs through a re-render.
   */
  initialMessages?: ThreadMessageLike[] | null;
  clearError?: () => void;
  /** Resync the thread with persisted state after replay/cancel settles. */
  onHistoryReload: () => Promise<void>;
}

interface UseRemoteRunReturn {
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

export function useRemoteRun({
  agent,
  runtime,
  api,
  conversationId,
  status,
  initialMessages,
  clearError,
  onHistoryReload,
}: UseRemoteRunOptions): UseRemoteRunReturn {
  const [isRunningRemotely, setIsRunningRemotely] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const cid = conversationId;
    const markRunFinished = () => {
      setIsRunningRemotely(false);
      markConversationIdle(cid);
    };
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setIsRunningRemotely(agent.isReplaying);
        markConversationRunning(cid);
      },
      onRunFinishedEvent: markRunFinished,
      onRunFailed: markRunFinished,
      onRunFinalized: () => {
        // Replay-only: pull persisted history so the thread reflects the
        // canonical post-run state. Local runs already have it in memory.
        if (agent.isReplaying) {
          void onHistoryReload();
        }
      },
    });

    if (status === ConversationStatus.MESSAGE_IN_PROGRESS) {
      // `resumeRun` calls `startRun(parentId)` → `resetHead(parentId)`,
      // which wipes all messages when parentId is null. Anchor on the
      // in-flight user prompt's id from /connect so the assistant
      // placeholder is appended after it instead of erasing it.
      // `runtime.thread.getState()` is unreliable here — `reset()` ran
      // synchronously above but the external-store repo only catches up
      // on the next render.
      const parentId = initialMessages?.at(-1)?.id ?? null;
      agent.enableReplayForNextRun();
      runtime.thread.resumeRun({ parentId });
    }

    return () => {
      sub.unsubscribe();
      agent.abortRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const onCancelRun = useCallback(() => {
    clearError?.();
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
        await onHistoryReload();
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
      }
    );
  }, [api, agent, conversationId, clearError, onHistoryReload]);

  return { isRunningRemotely, isCancelling, onCancelRun };
}
